// Static site data: services, case studies, industries, testimonials, clients.

import {
  Layers, Cpu, Cloud, BrainCircuit, Code2, Blocks, Workflow,
  Database, Server, Smartphone, Network, ShieldCheck, BarChart3,
  Truck, HeartPulse, Landmark, Coins, Building2, Factory,
} from "lucide-react";

export type ServiceCard = {
  id: string;
  title: string;
  description: string;
  Icon: typeof Layers;
  bullets: string[];
};

export const HOMEPAGE_SERVICES: ServiceCard[] = [
  {
    id: "sap-btp-cap",
    title: "SAP BTP & CAP",
    description: "Cloud-native SAP applications on Business Technology Platform with the Cloud Application Programming model.",
    Icon: Layers,
    bullets: ["CAP (Node.js & Java)", "BTP services", "HANA Cloud"],
  },
  {
    id: "sap-abap-rap",
    title: "SAP ABAP & RAP",
    description: "Modern ABAP development with the RESTful Application Programming model for S/4HANA extensions.",
    Icon: Cpu,
    bullets: ["S/4HANA extensions", "RAP business objects", "CDS views"],
  },
  {
    id: "cloud-devops",
    title: "Cloud & DevOps",
    description: "Production-grade AWS and Azure landing zones, CI/CD pipelines, and infrastructure-as-code.",
    Icon: Cloud,
    bullets: ["AWS / Azure", "Terraform", "GitHub Actions"],
  },
  {
    id: "genai-ml",
    title: "GenAI & ML",
    description: "Retrieval-augmented copilots, document understanding, and forecasting wired into enterprise systems.",
    Icon: BrainCircuit,
    bullets: ["RAG pipelines", "SAP AI Core", "Vector search"],
  },
  {
    id: "fullstack",
    title: "Full-Stack Engineering",
    description: "MERN, MEAN, Java + Spring Boot. Production-grade web and mobile apps engineered to scale.",
    Icon: Code2,
    bullets: ["MERN / MEAN", "Java + Spring", "React / Next.js"],
  },
  {
    id: "blockchain-defi",
    title: "Blockchain & DeFi",
    description: "Smart contracts, NFT marketplaces, and on-chain financial infrastructure built audit-ready.",
    Icon: Blocks,
    bullets: ["Solidity", "EVM dApps", "NFT platforms"],
  },
];

export const SAP_TRACK = [
  { title: "SAP BTP", desc: "Build cloud-native services on SAP Business Technology Platform.", Icon: Layers },
  { title: "SAP CAP (Node.js & Java)", desc: "Domain modeling with CDS, OData services, draft handling.", Icon: Workflow },
  { title: "SAP ABAP & RAP", desc: "Side-by-side and on-stack extensions with RAP business objects.", Icon: Cpu },
  { title: "SAP Fiori & UI5", desc: "Custom Fiori apps, Launchpad theming, FLP plugins.", Icon: Smartphone },
  { title: "SAP CPI & Integration", desc: "Cloud Integration flows, custom adapters, API management.", Icon: Network },
  { title: "S/4HANA Consulting", desc: "Greenfield implementations, conversions, brownfield extensions.", Icon: Database },
];

export const TECH_TRACK = [
  { title: "Cloud Engineering (AWS, Azure)", desc: "Landing zones, networking, IAM, observability.", Icon: Cloud },
  { title: "GenAI & ML Solutions", desc: "RAG copilots, document AI, forecasting, evaluations.", Icon: BrainCircuit },
  { title: "MERN & MEAN Stack", desc: "React / Angular front-ends with Node + Mongo / Express.", Icon: Code2 },
  { title: "Java & Spring Boot", desc: "Microservices, REST APIs, batch processing at enterprise scale.", Icon: Server },
  { title: "Blockchain & Smart Contracts", desc: "Solidity, EVM tooling, NFT and DeFi protocols.", Icon: Blocks },
  { title: "QA & Automation", desc: "Selenium, Playwright, Cypress, BDD pipelines, performance testing.", Icon: ShieldCheck },
];

export const ENGAGEMENT_MODELS = [
  {
    number: "01",
    title: "Staff Augmentation",
    subtitle: "Contractual / Project-Based",
    desc: "Pre-vetted engineers on KIBA Labs' payroll, deployed directly into your delivery team. We own HR, compliance, and payroll, you own the delivery. One engineer deployable in 2–3 business days. A team of 3–5 within 5 days.",
    points: [
      "Engineers on KIBA Labs payroll, zero hiring overhead for you",
      "1 engineer in 2–3 days · team of 3–5 in under 5 days",
      "Senior Tech Lead oversight included on every engagement",
      "Flexible durations, sprint-level to multi-year",
    ],
  },
  {
    number: "02",
    title: "Outsourced Delivery",
    subtitle: "Full-Cycle Project Ownership",
    desc: "We take end-to-end ownership, from architecture and design through build, QA, and deployment. Fixed Bid, T&M, or BOT (Build–Operate–Transfer) models available. Strong governance and measurable milestone-based outcomes.",
    points: [
      "Full-cycle ownership: architecture → build → QA → deployment",
      "Fixed Bid · T&M · BOT, flexible commercial structures",
      "Dedicated POD with Tech Lead, developers, and QA",
      "85%+ code coverage and agile sprint governance",
    ],
  },
  {
    number: "03",
    title: "Direct Placement",
    subtitle: "Client Payroll Engagement",
    desc: "Need to build an internal team? We source, screen, evaluate, and onboard engineers who then move directly onto your payroll. One-time fee model. All candidates go through our internal capstone program before being placed.",
    points: [
      "End-to-end screening, evaluation, and technical assessment",
      "Only pre-vetted candidates who've cleared our capstone",
      "Quick turnaround, typically 5–7 business days",
      "Long-term workforce continuity under your direct control",
    ],
  },
];

export type CaseStudy = {
  slug: string;
  title: string;
  industry: string;
  tech: string[];
  outcome: string;
  description: string;
  challenge: string;
  approach: string[];
  impact: string[];
};

export const CASE_STUDIES: CaseStudy[] = [
  {
    slug: "ich-life-sciences-track-trace",
    title: "Life Sciences, Track & Trace",
    industry: "Healthcare & Pharma",
    tech: ["SAP BTP", "CAP", "Fiori", "HANA"],
    outcome: "End-to-end serialised drug tracking across 18 distribution sites.",
    description: "Built an SAP BTP-native track-and-trace platform for a multi-country life sciences distributor.",
    challenge: "Regulator-mandated serialisation across 18 sites with mixed legacy ERPs.",
    approach: ["CAP services on BTP", "Fiori operator UIs", "Event Mesh integration with on-prem ERP", "HANA Cloud for analytics"],
    impact: ["100% lot traceability achieved", "Audit prep time cut 60%", "Recall response time under 4 hours"],
  },
  {
    slug: "dvdms-drug-supply-chain",
    title: "DVDMS, Drug Supply Chain",
    industry: "Government & Public Sector",
    tech: ["Java", "Spring Boot", "Angular", "Oracle"],
    outcome: "Statewide drug warehouse and dispatch platform serving 200+ public hospitals.",
    description: "Re-engineered a national-scale drug warehousing and distribution management system.",
    challenge: "Legacy stack causing daily reconciliation breaks at state warehouses.",
    approach: ["Microservices on Spring Boot", "Angular operations console", "Oracle backend", "Real-time stock visibility"],
    impact: ["Stock-out incidents down 72%", "Dispatch SLA improved to 99.4%", "Used by 200+ hospitals"],
  },
  {
    slug: "realize-365-medical-records-ai",
    title: "Realize-365, Medical Records AI",
    industry: "Healthcare & Pharma",
    tech: ["GenAI", "RAG", "Python", "AWS"],
    outcome: "Clinical summarisation copilot reducing chart review time by 45%.",
    description: "GenAI copilot that summarises longitudinal patient charts for primary care providers.",
    challenge: "Clinicians spending 30+ minutes per chart review across siloed EHRs.",
    approach: ["RAG over de-identified chart corpora", "Domain-tuned prompting", "Eval harness with clinician review", "AWS deployment with PHI controls"],
    impact: ["Chart review time down 45%", "Clinician satisfaction +38pts", "Zero PHI leakage in audits"],
  },
  {
    slug: "botswana-govt-licensing-platform",
    title: "Botswana Govt, Licensing Platform",
    industry: "Government & Public Sector",
    tech: ["MERN", "AWS", "PostgreSQL"],
    outcome: "National licensing portal handling 250k+ applications annually.",
    description: "Citizen-facing licensing portal for the Government of Botswana.",
    challenge: "Paper-based application process with multi-week turnaround.",
    approach: ["MERN stack with role-based workflows", "AWS hosting in regional zone", "Document upload & verification", "Inspector mobile app"],
    impact: ["Average TAT cut from 21 to 3 days", "250k+ applications processed", "100% digital audit trail"],
  },
  {
    slug: "sportiqo-fantasy-sports-nft",
    title: "SPORTIQO, Fantasy Sports NFT",
    industry: "Crypto & NFT Trading",
    tech: ["Solidity", "Polygon", "Next.js", "Node"],
    outcome: "On-chain fantasy sports platform with 80k+ NFT card holders.",
    description: "Fantasy sports platform with NFT player cards traded on Polygon.",
    challenge: "Combine real-world sport scoring with on-chain NFT economics fairly.",
    approach: ["Solidity contracts on Polygon", "Oracle-fed scoring", "Next.js front-end", "Custodial + non-custodial wallets"],
    impact: ["80k+ NFT holders onboarded", "Sub-second card trades", "Audit-clean smart contracts"],
  },
  {
    slug: "coinswitch-pro-crypto-exchange",
    title: "CoinSwitch Pro, Crypto Exchange",
    industry: "Fintech & DeFi",
    tech: ["Java", "Kafka", "React", "AWS"],
    outcome: "Pro-trader exchange surface launched in under 6 months.",
    description: "Built the pro-trader trading surface for a top Indian crypto exchange.",
    challenge: "Sub-100ms order book updates at exchange scale.",
    approach: ["Java microservices", "Kafka for order events", "React trading terminal", "AWS multi-AZ"],
    impact: ["<80ms order book latency", "Launched in 5 months", "Zero downtime in first quarter"],
  },
  {
    slug: "ai-tmr-sales-chatbot",
    title: "AI-TMR, Sales Chatbot",
    industry: "Insurance Services",
    tech: ["GenAI", "LLM", "Python", "Azure"],
    outcome: "Inside-sales copilot lifting qualified-lead conversion by 22%.",
    description: "GenAI copilot that helps insurance inside-sales reps qualify leads in real time.",
    challenge: "High agent attrition + inconsistent qualification scripts.",
    approach: ["LLM playbook orchestration", "CRM integration", "Real-time call assist", "Azure deployment"],
    impact: ["Conversion +22%", "Onboarding time for new reps cut 50%", "100% call audit coverage"],
  },
  {
    slug: "voice-of-customer-ai",
    title: "Voice of Customer AI",
    industry: "Supply Chain & Logistics",
    tech: ["NLP", "Python", "Snowflake"],
    outcome: "Daily NPS-driver insights from 1M+ customer interactions.",
    description: "NLP pipeline that mines customer interactions for emerging issues.",
    challenge: "Service teams flooded with feedback they could not categorise at scale.",
    approach: ["Topic modeling + sentiment", "Snowflake warehouse", "Daily executive digest", "Trend dashboards"],
    impact: ["Issue detection lag down from 14 days to <24h", "NPS up 9pts", "Used daily by C-suite"],
  },
];

export const INDUSTRIES = [
  {
    slug: "healthcare",
    title: "Healthcare & Life Sciences",
    desc: "From pharmaceutical serialisation to AI-powered clinical intelligence   we build regulated, audit-ready systems that keep drugs, data, and patients safe.",
    Icon: HeartPulse,
    caseStudy: "ich-life-sciences-track-trace",
    highlights: [
      "Built cloud-based pharmaceutical serialisation platforms enabling secure exchange of commissioning, de-commissioning and track-and-trace data between partners via SOAP, AS2 and SFTP",
      "Deployed private blockchain networks to store and authenticate every drug transaction with tamper-proof cryptographic records across multi-country distribution chains",
      "Engineered end-to-end drug and vaccine supply chain systems covering demand, manufacturing, quality assurance, warehousing, and last-mile delivery with full audit trails",
      "Developed AI-driven predictive models for vaccine demand forecasting, giving manufacturers a clear view of upcoming orders quarters in advance",
      "Delivered GenAI-powered medical records platforms using RAG and OCR to automate case-valuation, intelligent search, and clinical data extraction for healthcare professionals",
    ],
  },
  {
    slug: "supply-chain",
    title: "Supply Chain & Logistics",
    desc: "National-scale warehouse management, AI-powered demand forecasting, and voice-of-customer intelligence for distributors moving millions of SKUs.",
    Icon: Truck,
    caseStudy: "voice-of-customer-ai",
    highlights: [
      "Built a statewide drug warehousing and dispatch platform serving 200+ public hospitals with real-time inventory visibility and cryptographic audit trails",
      "Deployed NLP pipelines that mine over a million customer interactions daily to surface emerging pain points and auto-generate optimised agent scripts",
      "Integrated SAP-native demand forecasting modules reducing overstock and stockout events for national-scale distributors",
      "Delivered full supply chain visibility from indent creation through manufacturing, quality assurance, logistics, and distribution to end users",
      "Built predictive AI models for seasonal and demand-driven inventory planning across multi-tier distribution networks",
    ],
  },
  {
    slug: "fintech",
    title: "Fintech & DeFi",
    desc: "Exchange-grade trading infrastructure, on-chain settlement, and KYC pipelines built for the speed and compliance demands of modern financial markets.",
    Icon: BarChart3,
    caseStudy: "coinswitch-pro-crypto-exchange",
    highlights: [
      "Built a high-frequency order matching engine with smart contracts handling BTC, ETH and USDT currency pairs   delivered in under 6 months",
      "Engineered Kafka-powered event streaming for real-time trade settlement, order book updates and portfolio reconciliation at exchange scale",
      "Integrated KYC and AML pipelines for regulatory compliance across Indian and international financial markets",
      "Implemented Polygon Layer 2 and oracle integrations for low-cost, high-throughput on-chain asset exchange and trustless data feeds",
      "Designed and deployed permissioned enterprise blockchain networks for secure, auditable financial transaction recording",
    ],
  },
  {
    slug: "insurance",
    title: "Insurance Services",
    desc: "AI copilots that qualify leads, handle objections, and surface policy insights   turning inside-sales teams into high-conversion engines.",
    Icon: ShieldCheck,
    caseStudy: "ai-tmr-sales-chatbot",
    highlights: [
      "Delivered a multilingual AI sales engagement assistant that automates lead qualification and lifts conversion rates by over 22%",
      "Built hyper-personalised interaction flows using customer persona profiles, behavioural data and dynamic product information retrieval",
      "Auto-generated persuasion and objection-handling scripts from historical high-conversion call recordings using LLMs",
      "Developed on-chain flight insurance settlement using oracle and Layer 2 networks for instant, trustless claim payouts",
      "Engineered voice-of-customer pipelines transforming call recordings into actionable coaching insights for sales teams",
    ],
  },
  {
    slug: "government",
    title: "Government & Public Sector",
    desc: "Citizen-facing platforms and national-scale infrastructure engineered for the reliability, security, and compliance that public institutions demand.",
    Icon: Landmark,
    caseStudy: "botswana-govt-licensing-platform",
    highlights: [
      "Built a centralised permit and licence generation platform handling over 250,000 applications annually across multiple ministries",
      "Deployed a service registry with IP whitelisting and blacklisting ensuring only authorised entities can register APIs and microservices",
      "Engineered specialised API gateways for IoT devices, cloud connectors, and citizen-facing traffic   each optimised for its request type",
      "Delivered a national drug warehouse management system for public hospitals with end-to-end supply chain visibility and cryptographic audit",
      "Designed for zero-downtime citizen services on AWS   scalable, multi-ministry, and built to government security standards",
    ],
  },
  {
    slug: "crypto",
    title: "Crypto & NFT Trading",
    desc: "On-chain protocols, NFT marketplaces, and exchange infrastructure   audit-clean Solidity from concept to mainnet.",
    Icon: Coins,
    caseStudy: "sportiqo-fantasy-sports-nft",
    highlights: [
      "Engineered a full pro-trader crypto exchange order book, depth charts, matching engine, and Solidity smart contracts supporting BTC/USD, ETH/USD and USDT/USD pairs across Ethereum and Solana",
      "Built a high-performance order matching engine that pairs buy and sell orders from the order book in real time, with instant database sync on every successful match",
      "Launched a performance-driven NFT trading platform with dynamic player card minting, live valuation tied to athlete performance, and 80k+ active card holders",
      "Engineered proprietary in-platform digital currency systems enabling seamless, secure peer-to-peer NFT acquisition and trading",
      "Implemented enterprise-grade permissioned blockchain networks using Hyperledger Fabric for auditable, private transaction recording across consortium participants",
    ],
  },
];

export const INDUSTRIES_STRIP = [
  { name: "Supply Chain", Icon: Truck },
  { name: "Healthcare & Pharma", Icon: HeartPulse },
  { name: "Fintech", Icon: BarChart3 },
  { name: "Insurance", Icon: ShieldCheck },
  { name: "Government", Icon: Landmark },
  { name: "Crypto & NFT", Icon: Coins },
];

export const STATS = [
  { value: 7, suffix: "+", label: "Years Experience" },
  { value: 20, suffix: "+", label: "Global Clients" },
  { value: 10, suffix: "+", label: "Countries" },
  { value: 30, suffix: "+", label: "SAP Projects" },
  { value: 50, suffix: "+", label: "Technologies" },
];

export const RESOURCE_FOOTPRINT = [
  { vertical: "SAP CAPM / Fiori / BTP", deployed: 10, bench: "4–6" },
  { vertical: "SAP ABAP / RAP", deployed: 11, bench: "12+" },
  { vertical: "SAP CPI / Adapters / Connectors", deployed: 3, bench: "2" },
  { vertical: "Java / AWS / Microservices", deployed: 8, bench: "4" },
  { vertical: "MERN / MEAN Stack", deployed: 12, bench: "6" },
  { vertical: "QA / Test Automation", deployed: 4, bench: "2" },
];

export const CLIENTS = [
  "SAP Labs India", "SAP Labs Bulgaria", "Vertex Inc.", "NTT Data",
  "FSoft / FPT Software", "SEAL Infotech", "Deviare Africa", "Inq Africa",
  "Nu10 Technologies", "Rejolut Technologies", "Educurve India",
  "HCL Technologies", "ITC Infotech",
  "Usterra", "mTilda", "IASA", "Mech Solutions Ltd", "MiDS",
  "Book Einstein", "CheckMark Cyber Botswana", "Bahwan CyberTek", "easyReg",
];

export const TESTIMONIALS = [
  {
    quote: "KIBA Lab has a decent group of developers, engineers, and specialized team that plays out the work expertly and with dedication to finish assignments inside the booked time and to customer fulfilment. I would unequivocally recommend them for any organisation searching for acceptable contractors in the ICT space.",
    author: "Prince Motshwari",
    title: "Managing Director",
    company: "Vigilan Link Pty Ltd",
    project: "Central License & Permit Platform, Government of Botswana",
    industry: "Government",
    accentColor: "#34D399",
  },
  {
    quote: "Kiba Labs has consistently demonstrated a deep understanding of our unique project and resourcing needs and brings a commitment to helping us achieve our project goals. Their ability to provide top-notch talent, even for highly specialized roles, has been instrumental in the success of our projects. We certainly will engage them for a long term in future too.",
    author: "Gururaj Potnis",
    title: "Technology Lead",
    company: "Nu10 Technologies",
    project: "CoinSwitch Pro, NFT Marketplace, Sport Trading Platform",
    industry: "Blockchain",
    accentColor: "#A78BFA",
  },
  {
    quote: "The Kiba team has excelled in developing the entire DVDMS project, showcasing expertise in gathering and implementing requirements while maintaining a strong focus on client engagement.",
    author: "Mohit Kumar",
    title: "Manager Systems",
    company: "DVDMS Project",
    project: "Drug & Vaccine Distribution Management System",
    industry: "Life Science",
    accentColor: "#FFC93A",
  },
  {
    quote: "KIBA Labs onboarded a four-engineer SAP CAP squad in under two weeks. They shipped production code in their third sprint. That is rare.",
    author: "Delivery Director",
    title: "Delivery Director",
    company: "Global SAP Consulting Partner",
    project: "SAP BTP Extension, Supply Chain",
    industry: "SAP",
    accentColor: "#60A5FA",
  },
  {
    quote: "We treat them as an extension of our team not a vendor. Their bench is real and the engineers are full-time. It shows in the code quality.",
    author: "VP Engineering",
    title: "VP Engineering",
    company: "European System Integrator",
    project: "SAP Integration & ABAP Modernisation",
    industry: "SAP",
    accentColor: "#60A5FA",
  },
];

export const CORE_VALUES = [
  { title: "Innovation", desc: "We ship what comes next, not what was popular last year. When the problem demands Go and gRPC, we don't use Java just because everyone else does." },
  { title: "Integrity", desc: "Honest scoping. Transparent reporting. No surprise invoices. We tell you what we can deliver and we deliver exactly that." },
  { title: "Collaboration", desc: "Our engineers work shoulder-to-shoulder with your delivery team, embedded in your sprints, your tools, and your culture from day one." },
  { title: "Quality", desc: "85%+ code coverage standard. Full unit tests on every OData endpoint. Code that survives audits, regulators, and the next engineer who reads it." },
];

export const PARTNER_REASONS = [
  { metric: "90%", label: "SAP project conversion rate" },
  { metric: "1–2 wks", label: "Onboarding TAT" },
  { metric: "100%", label: "Full-time payroll professionals" },
  { metric: "30+", label: "Active image.pngdeployments" },
];

export const ABOUT_STATS = [
  { value: "5+", label: "Successful AI Solutions Delivered" },
  { value: "60%", label: "SAP-focused practice" },
  { value: "7+", label: "Years Life Science using SAP Solutions" },
  { value: "20+", label: "Global clients across 10+ countries" },
  { value: "90%", label: "SAP project conversion rate" },
];

export const DIFFERENTIATORS = [
  {
    number: "01",
    title: "Deep SAP engineering roots",
    desc: "Our founder spent 8 years inside SAP's core engineering ecosystem, among the first developers in India to build production SAP CAPM and BTP applications when the platform was nascent. That first-hand, inside-out knowledge of how SAP systems actually work is embedded in every engineer we train.",
  },
  {
    number: "02",
    title: "Bench, not a marketplace",
    desc: "Every engineer is on KIBA Labs payroll. Not a freelancer. Not a subcontractor. They have gone through our internal capstone program, 30+ real use cases built in-house. When you get a KIBA Labs engineer, you get someone who has already shipped production code in your stack.",
  },
  {
    number: "03",
    title: "2–3 day deployment",
    desc: "One engineer ready in 2–3 business days. A team of 3–5 within 5 days. We maintain a live bench specifically for rapid deployment, not a theoretical capacity. Our 90% SAP project retention rate means clients rarely return an engineer once deployed.",
  },
  {
    number: "04",
    title: "Senior oversight on every engagement",
    desc: "Every deployment includes a Senior Tech Lead who remains allocated across the engagement for architecture decisions, code reviews, and escalations, even when day-to-day management sits with the client. Engineers do not go in without senior oversight.",
  },
];

export const OPEN_ROLES = [
  {
    id: 1,
    slug: "sap-capm-btp-developer",
    title: "SAP CAPM / BTP Developer",
    department: "SAP Practice",
    location: "Bengaluru, India",
    type: "Full Time",
    experience: "3–6 years",
    tags: ["SAP BTP", "CAP Node.js", "CAP Java", "Fiori", "XSUAA", "HANA Cloud"],
    overview: "We are looking for an experienced SAP CAP/BTP developer to join our SAP Practice and work directly on enterprise engagements with global system integrators. You will build multi-tenant CAPM applications, CPI integration flows, and Fiori interfaces for clients running S/4HANA, from requirements through to production deployment.",
    whatYoullDo: [
      "Design and develop multi-tenant SAP CAP applications using Node.js and Java on SAP BTP",
      "Build and maintain CPI integration flows covering REST, SOAP, AS2, and SFTP adapters with robust error handling",
      "Develop Fiori Elements and freestyle UI5 applications consuming OData V4 endpoints",
      "Configure XSUAA, Destination Service, SAP IAS, and Cloud Connectors for secure BTP deployments",
      "Integrate SAP BTP applications with HANA Cloud, S/4HANA, and third-party systems via Event Mesh",
      "Participate in architecture reviews, sprint planning, and client-facing delivery calls",
      "Mentor junior engineers and contribute to our internal SAP capstone library",
    ],
    requirements: [
      "3–6 years of hands-on SAP development experience with at least 2 years on SAP BTP/CAP",
      "Proficiency in CAP (Node.js or Java), CDS modelling, and OData V4 service development",
      "Experience with XSUAA, Destination Service, and BTP security configuration",
      "Familiarity with SAP Cloud Integration (CPI) and building integration adapters",
      "Strong understanding of S/4HANA data models and SAP enterprise architecture",
      "Good communication skills, you'll work directly with SI delivery teams",
    ],
    niceToHave: [
      "SAP BTP certification or formal SAP training",
      "Experience with SAP Event Mesh, SAP Business Application Studio",
      "Knowledge of SAP ICH / Life Sciences serialisation processes",
      "Fiori Launchpad configuration and custom theme development",
    ],
  },
  {
    id: 2,
    slug: "sap-abap-rap-developer",
    title: "SAP ABAP / RAP Developer",
    department: "SAP Practice",
    location: "Bengaluru, India",
    type: "Full Time",
    experience: "4–8 years",
    tags: ["ABAP", "RAP", "OData", "S/4HANA", "CDS Views", "BAdI"],
    overview: "We need a strong ABAP developer to deliver S/4HANA extension and migration work for enterprise clients. You will write clean, performant ABAP code, both classic and modern RESTful ABAP Programming (RAP), and help clients move legacy ABAP workloads to a cloud-ready architecture.",
    whatYoullDo: [
      "Develop ABAP extensions, custom programs, and enhancements on S/4HANA using modern ABAP syntax",
      "Build RESTful ABAP Programming (RAP) services exposing CDS-based OData V4 APIs",
      "Create and optimise CDS views for reporting, analytics, and API consumption",
      "Implement BAdIs, user exits, and custom enhancements following clean-core principles",
      "Migrate legacy ABAP reports and function modules to ABAP RESTful Application Programming Model",
      "Write unit-testable, well-documented ABAP code that passes code review at SAP quality standards",
      "Support performance tuning, database query optimisation, and critical SAP basis tasks",
    ],
    requirements: [
      "4–8 years of ABAP development on SAP ECC or S/4HANA",
      "Solid understanding of ABAP OO, CDS views, BOPF/RAP, and the ABAP RESTful Application Programming Model",
      "Experience with OData V2/V4 service development and Fiori backend configuration",
      "Good grasp of S/4HANA data model changes from ECC (universal journal, material ledger)",
      "Familiarity with SAP transport management and delivery processes",
    ],
    niceToHave: [
      "Experience with ABAP Unit testing and Test-Driven Development (TDD)",
      "SAP S/4HANA certification",
      "Exposure to SAP BTP side-by-side extensibility patterns",
      "Knowledge of ABAP for SAP ICH / serialisation processes in Life Sciences",
    ],
  },
  {
    id: 3,
    slug: "full-stack-engineer-mern",
    title: "Full Stack Engineer (MERN)",
    department: "Tech Practice",
    location: "Bengaluru, India",
    type: "Full Time",
    experience: "2–5 years",
    tags: ["React", "Node.js", "MongoDB", "AWS", "TypeScript", "REST APIs"],
    overview: "We are hiring a full-stack MERN engineer to ship complete, domain-specific product builds, spanning Supply Chain, Insurance, EdTech, and Life Sciences. You will own features end-to-end: API design, frontend implementation, database schema, and deployment, using AI-assisted development workflows as standard practice.",
    whatYoullDo: [
      "Build and maintain full-stack features using React (TypeScript), Node.js, and MongoDB",
      "Design RESTful and GraphQL APIs with proper authentication, rate limiting, and error handling",
      "Implement responsive, accessible UIs using Tailwind CSS or component libraries",
      "Write unit and integration tests achieving 85%+ code coverage, non-negotiable",
      "Deploy and operate services on AWS (Lambda, ECS, RDS, S3) using Infrastructure-as-Code",
      "Contribute to AI-assisted development workflows: prompting, code review, and LLM tooling",
      "Participate in domain-specific capstone projects during bench periods",
    ],
    requirements: [
      "2–5 years of full-stack development experience with React and Node.js",
      "Strong TypeScript proficiency on both frontend and backend",
      "Experience with MongoDB (aggregation pipelines, indexing strategies)",
      "Comfortable with REST API design, JWT authentication, and WebSocket real-time patterns",
      "Working knowledge of AWS services: Lambda, API Gateway, S3, RDS, or DynamoDB",
      "Git, CI/CD pipelines, and test automation as standard practice",
    ],
    niceToHave: [
      "Experience in regulated industries: pharma, insurance, logistics, or EdTech",
      "Familiarity with AI/GenAI tooling: LangChain, OpenAI APIs, RAG patterns",
      "Exposure to SAP BTP or integration with SAP backends",
      "React Native or mobile development experience",
    ],
  },
  {
    id: 4,
    slug: "aws-cloud-engineer",
    title: "AWS Cloud Engineer",
    department: "Cloud Practice",
    location: "Bengaluru, India / Remote",
    type: "Full Time",
    experience: "3–6 years",
    tags: ["AWS", "Lambda", "EKS", "Terraform", "CDK", "CloudWatch"],
    overview: "We are looking for an AWS Cloud Engineer to design, build, and operate cloud infrastructure for our enterprise product and client delivery engagements. You will go from architecture to live deployment, with observability, CI/CD, and cost-optimisation built in from the first commit, not bolted on afterwards.",
    whatYoullDo: [
      "Design and implement AWS infrastructure using Terraform and CDK (Infrastructure-as-Code)",
      "Build serverless APIs on Lambda and API Gateway with DynamoDB, RDS, and S3 backends",
      "Operate containerised microservices on ECS/EKS with service mesh, auto-scaling, and health monitoring",
      "Build and maintain end-to-end CI/CD pipelines using GitHub Actions and AWS CodePipeline",
      "Set up observability stacks: CloudWatch dashboards, Grafana, X-Ray tracing, PagerDuty alerting",
      "Enforce security best practices: IAM least-privilege, VPC design, secrets management, and compliance controls",
      "Conduct cost reviews and implement savings plans, rightsizing, and architectural optimisations",
    ],
    requirements: [
      "3–6 years of hands-on AWS cloud engineering experience in production environments",
      "Proficiency in Terraform or AWS CDK for infrastructure-as-code",
      "Solid experience with ECS/EKS container orchestration and Docker",
      "Strong knowledge of AWS networking: VPC, Route 53, ALB, CloudFront, Transit Gateway",
      "Experience with CI/CD pipelines and GitOps workflows",
      "AWS Solutions Architect Associate (or Professional), or equivalent real-world experience",
    ],
    niceToHave: [
      "AWS DevOps Engineer Professional certification",
      "Experience with SAP BTP side-by-side deployments on AWS",
      "Kubernetes operator development and Helm chart management",
      "FinOps practice: tagging strategy, Savings Plans, Cost Explorer automation",
    ],
  },
  {
    id: 5,
    slug: "ai-genai-engineer",
    title: "AI / GenAI Engineer",
    department: "AI Practice",
    location: "Bengaluru, India / Remote",
    type: "Full Time",
    experience: "2–5 years",
    tags: ["Python", "LangChain", "RAG", "AWS SageMaker", "MLflow", "LLMs"],
    overview: "We are hiring an AI/GenAI engineer to build production-grade AI systems, not notebook experiments. You will work on RAG pipelines, LLMOps infrastructure, and ML model serving for enterprise clients in healthcare, supply chain, and financial services. If you care about hallucination rates, token costs, and model drift as much as benchmark scores, you'll fit right in.",
    whatYoullDo: [
      "Build end-to-end RAG pipelines using LangChain/LlamaIndex with vector stores (Pinecone, pgvector, OpenSearch)",
      "Design and implement LLMOps infrastructure: prompt versioning, evaluation harnesses, hallucination guards",
      "Train and deploy ML models on AWS SageMaker with managed endpoints and A/B routing",
      "Build MLOps platforms with automated model versioning, drift detection, and retraining pipelines using MLflow and DVC",
      "Develop GenAI-powered features: document intelligence, intelligent search, automated extraction, and recommendation",
      "Monitor production LLM APIs for cost, latency, and quality, and optimise all three",
      "Work closely with domain experts to understand the business problem before choosing the model",
    ],
    requirements: [
      "2–5 years of software engineering experience, with at least 1 year on production AI/ML systems",
      "Strong Python proficiency: async patterns, type hints, packaging, and testing",
      "Hands-on experience building RAG systems with LangChain, LlamaIndex, or similar frameworks",
      "Familiarity with LLM APIs (OpenAI, Anthropic, AWS Bedrock) and prompt engineering best practices",
      "Experience with AWS SageMaker or equivalent ML training and serving infrastructure",
      "Understanding of vector databases, embedding models, and semantic search architecture",
    ],
    niceToHave: [
      "MLOps tooling: MLflow, DVC, Weights & Biases",
      "Experience with open-source LLMs (Llama 3, Mistral) for privacy-sensitive deployments",
      "Knowledge of regulated industries: clinical NLP, financial document processing",
      "Contributions to open-source AI/ML projects",
    ],
  },
  {
    id: 6,
    slug: "qa-automation-engineer",
    title: "QA / Automation Engineer",
    department: "QA Practice",
    location: "Bengaluru, India",
    type: "Full Time",
    experience: "2–4 years",
    tags: ["Selenium", "Playwright", "Jest", "Pytest", "CI/CD", "API Testing"],
    overview: "We hold an 85%+ code coverage standard across all our products and client deliveries. As our QA Automation Engineer, you will build and maintain the test harnesses that enforce that standard, from unit tests and API tests to end-to-end UI automation, and integrate them into our CI pipelines so every merge is validated automatically.",
    whatYoullDo: [
      "Design and implement automated test suites: unit, integration, API, and end-to-end",
      "Build Playwright and Selenium-based UI automation for MERN and SAP Fiori applications",
      "Write API test suites using Postman/Newman, REST Assured, or similar",
      "Integrate test automation into GitHub Actions and AWS CodePipeline CI/CD workflows",
      "Set up and maintain test reporting dashboards with coverage metrics and trend tracking",
      "Perform performance and load testing using k6 or JMeter on critical API endpoints",
      "Work closely with developers to define testability requirements during feature design",
    ],
    requirements: [
      "2–4 years of QA automation experience on web applications",
      "Proficiency in Playwright or Selenium WebDriver for UI automation",
      "Experience writing API tests with REST-based tooling",
      "Comfortable with JavaScript/TypeScript or Python for test scripting",
      "Working knowledge of CI/CD pipelines and test gate integration",
      "Strong analytical thinking and ability to identify edge cases early",
    ],
    niceToHave: [
      "Experience testing SAP Fiori applications",
      "Performance testing with k6, Gatling, or JMeter",
      "ISTQB Foundation certification",
      "Familiarity with accessibility testing (WCAG 2.1)",
    ],
  },
];

export type NavChild = { to: string; label: string; desc: string };
export type NavLink = { to: string; label: string; children?: NavChild[] };

export const NAV_LINKS: NavLink[] = [
  { to: "/", label: "Home" },
  {
    to: "/services",
    label: "Services",
    children: [
      { to: "/services/fullstack", label: "Full-Stack & Product Engineering", desc: "MERN, Spring Boot, React Native, AWS, DevOps" },
      { to: "/services/sap",       label: "SAP Practice",                    desc: "BTP, ABAP, RAP, Fiori, CPI, S/4HANA, SuccessFactors" },
      { to: "/services/ai",        label: "AI & Machine Learning",            desc: "RAG, LLMOps, MLOps, GenAI, Vector DBs" },
      { to: "/services/consulting",label: "Consulting & Staff Augmentation",  desc: "Staff aug, ODC, SI partnerships, Tech consulting" },
      { to: "/services/domains",   label: "Domain Expertise",                 desc: "Healthcare, Fintech, Supply Chain, Insurance & more" },
    ],
  },
  {
    to: "/products",
    label: "Products",
    children: [
      { to: "/products/aidiph",       label: "AIDIPH",        desc: "AI-driven HCM and Payroll platform" },
      { to: "/products/indentledger", label: "IndentLedger",  desc: "Smart indent and hardware supply ledger" },
      { to: "/products/proctorly",    label: "Proctorly",     desc: "AI-embedded proctoring SaaS" },
      { to: "/products/onesap",       label: "One SAP",       desc: "Conversational AI for all SAP solutions" },
    ],
  },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/industries",   label: "Industries" },
  { to: "/about",        label: "About" },
  { to: "/partner",      label: "Partner With Us" },
  { to: "/careers",      label: "Careers" },
  { to: "/events",       label: "Events" },
  { to: "/blog",         label: "Blog" },
  { to: "/contact",      label: "Contact" },
];

export { Building2, Factory };

/* ─────────────────────── EVENTS ─────────────────────── */
export type EventStatus = "upcoming" | "registration_open" | "ongoing" | "past";

export type KibaEvent = {
  slug: string;
  title: string;
  tagline: string;
  type: string;
  date: string;
  endDate?: string;
  duration: string;
  location: string;
  mode: "In-Person" | "Virtual" | "Hybrid";
  status: EventStatus;
  accentColor: string;
  coverImage?: string;          // top image on card
  coverPosition?: string;       // CSS object-position for the card image
  description: string;
  highlights: string[];
  schedule?: { time: string; title: string; desc: string }[];
  prizes?: { rank: string; reward: string }[];
  eligibility?: string;
  registrationUrl?: string;
  tags: string[];
};

export const EVENTS: KibaEvent[] = [
  {
    slug: "buildthon-2026",
    title: "Build#thon 2026",
    tagline: "Real problems. AI-first answers.",
    type: "Hackathon",
    date: "2026-10-31",
    duration: "9:00 AM to 8:00 PM IST",
    location: "STPI Building, Patna, Bihar",
    mode: "In-Person",
    status: "registration_open",
    accentColor: "#FFC93A",
    coverImage: "/buildthon/abhimanyu-portrait.jpg",
    coverPosition: "50% 12%",
    description:
      "Build#thon 2026 is KIBA Labs' one-day, in-person AI hackathon in Patna. Teams turn AI into working prototypes for problems people in India face every day: learning in their own language, healthcare advice, fraud, farming and crowded cities. No slide decks. A prototype that a real user could try tomorrow.",
    highlights: [
      "One day, in person, at the STPI Building, Patna",
      "10 AI problem statements, picked live on a wheel",
      "About 3 hours to build a working prototype",
      "₹1,50,000 cash prize pool",
      "T-shirt, cap, coffee tumbler and certificate for every participant",
      "Free lunch, snacks and energy drinks",
    ],
    schedule: [
      { time: "09:00 AM", title: "Registration desk opens", desc: "Check in with your own entry pass and a government photo ID." },
      { time: "10:00 AM", title: "Projects assigned", desc: "Once a group of teams is checked in, projects are picked live on the wheel." },
      { time: "10:30 AM", title: "Build starts", desc: "Create your branch and commit as you go." },
      { time: "01:30 PM", title: "Time up", desc: "Final code must be on your branch." },
      { time: "01:30 PM", title: "Lunch", desc: "Free lunch from 1:30 to 2:30 PM." },
      { time: "03:30 PM", title: "Presentations, demos and results", desc: "5 minutes to demo, 3 minutes of jury questions." },
      { time: "08:00 PM", title: "Event closes", desc: "" },
    ],
    prizes: [
      { rank: "🥇 Grand Champion", reward: "₹65,000" },
      { rank: "🥈 Runner Up", reward: "₹40,000" },
      { rank: "🥉 Second Runner Up", reward: "₹25,000" },
      { rank: "🏅 Third Runner Up", reward: "₹10,000" },
      { rank: "🏅 Fourth Runner Up", reward: "₹10,000" },
    ],
    eligibility: "College students from any year, college and branch, working professionals, and graduates looking for a job. Teams of up to 3 or solo. Priority goes to 3rd and 4th year students and non-students.",
    registrationUrl: "https://buildathon.kibalabs.in/register",
    tags: ["Hackathon", "AI", "Patna", "Students", "Professionals"],
  },
  {
    slug: "cricketmania",
    title: "CricketMania",
    tagline: "Squad vs squad. Bat, bowl, bond.",
    type: "Sports",
    date: "",                       // TBA: set an ISO date (YYYY-MM-DD) once confirmed
    duration: "To be announced",
    location: "Venue to be announced",
    mode: "In-Person",
    status: "upcoming",
    accentColor: "#4ade80",
    coverImage: "/events/cricketmania/cover.jpg",
    description:
      "CricketMania is our inter squad cricket tournament, a friendly one where KIBA Labs squads take each other on. Expect rivalry, loud cheers and team bonding that goes well beyond the codebase.",
    highlights: [
      "Squad versus squad matches",
      "Team bonding outside of work",
      "Open to all KIBA Labs squads",
    ],
    tags: ["Cricket", "Squads", "Team Bonding"],
  },
];

/* True while at least one event with a confirmed date is still to take place (used to make the Events nav item blink).
   Events with no date yet (TBA) do not count, and it switches itself off once the last event day has passed. */
export const hasUpcomingEvent = (now: Date = new Date()): boolean =>
  EVENTS.some(e => {
    if (e.status === "past") return false;
    const last = e.endDate || e.date;
    if (!last) return false;
    const end = new Date(`${last}T23:59:59`);
    return !isNaN(end.getTime()) && end.getTime() >= now.getTime();
  });
